import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { Fine, FinesListResponse } from './types';

const KEY = {
  all: ['fines'] as const,
  list: (p: Record<string, unknown>) => ['fines', 'list', p] as const,
  summary: ['fines', 'summary'] as const,
};

export function useFinesList(params: { page?: number; limit?: number; vehicleId?: string; status?: string; severity?: string; sort?: string } = {}) {
  return useQuery<FinesListResponse>({
    queryKey: KEY.list(params),
    queryFn: async () => {
      const { data } = await api.get<FinesListResponse>('/fines', { params });
      return data;
    },
  });
}

export function useFinesSummary() {
  return useQuery({
    queryKey: KEY.summary,
    queryFn: async () => {
      const { data } = await api.get<{ data: Record<string, unknown> }>('/fines/summary');
      return data.data;
    },
  });
}

export function useCreateFine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Record<string, unknown>) => {
      const { data } = await api.post<{ data: Fine }>('/fines', input);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.all }),
  });
}

export function useDeleteFine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/fines/${id}`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.all }),
  });
}

export function useIndicateDriver() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      driverId,
      indicationDeadline,
    }: {
      id: string;
      driverId: string;
      indicationDeadline?: string;
    }) => {
      const { data } = await api.post<{ data: Fine }>(`/fines/${id}/indicate-driver`, {
        driverId,
        indicationDeadline,
      });
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.all }),
  });
}

export function useTakeFineResponsibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<{ data: Fine }>(`/fines/${id}/take-responsibility`);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.all }),
  });
}

export function usePayFine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, paidAmountCents }: { id: string; paidAmountCents: number }) => {
      const { data } = await api.post<{ data: Fine }>(`/fines/${id}/pay`, { paidAmountCents });
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.all }),
  });
}

export function useCancelFine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<{ data: Fine }>(`/fines/${id}/cancel`);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.all }),
  });
}
